'use client'

/**
 * NtfyNotificationProvider
 * 
 * React context provider for ntfy.sh push notifications
 * Initializes the notification service when user is authenticated
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { ntfyService, NtfyMessage, ConnectionState } from './ntfy-notification-service'
import { createClient } from '@/lib/supabase/client'
import { useToast } from '@/components/ui/toast'
import type { User } from '@supabase/supabase-js'

interface NtfyContextValue {
    connectionState: ConnectionState
    lastNotification: NtfyMessage | null
    isConnected: boolean
}

const NtfyContext = createContext<NtfyContextValue>({
    connectionState: { status: 'disconnected' },
    lastNotification: null,
    isConnected: false
})

export function useNtfyNotifications() {
    return useContext(NtfyContext)
}

interface NtfyNotificationProviderProps {
    children: React.ReactNode
}

export function NtfyNotificationProvider({ children }: NtfyNotificationProviderProps) {
    const [user, setUser] = useState<User | null>(null)
    const [connectionState, setConnectionState] = useState<ConnectionState>({ status: 'disconnected' })
    const [lastNotification, setLastNotification] = useState<NtfyMessage | null>(null)
    const toast = useToast()

    // Check auth state on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const supabase = await createClient()
                const { data: { user } } = await supabase.auth.getUser()
                setUser(user)

                // Listen for auth changes
                const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
                    setUser(session?.user ?? null)
                })

                return () => subscription.unsubscribe()
            } catch {
                setUser(null)
            }
        }

        checkAuth()
    }, [])

    const handleNotification = useCallback((message: NtfyMessage) => {
        setLastNotification(message)

        // Show in-app toast for vendor notifications
        const type = message.extras?.['X-Notification-Type']
        const title = message.title ?? 'Notification'
        const body = message.message ?? ''

        if (type === 'card_update_request') {
            toast.warning(`${title}: ${body}`)
        } else if (type === 'transaction_alert') {
            const isSuccess = title.includes('Complete')
            if (isSuccess) {
                toast.success(`${title}: ${body}`)
            } else {
                toast.error(`${title}: ${body}`)
            }
        } else {
            toast.info(`${title}: ${body}`)
        }
    }, [toast])

    useEffect(() => {
        if (!user) {
            ntfyService.stop()
            setConnectionState({ status: 'disconnected' })
            return
        }

        // Start the notification service
        ntfyService.start()

        // Subscribe to notifications
        const unsubscribe = ntfyService.onNotification(handleNotification)

        // Poll connection state
        const interval = setInterval(() => {
            setConnectionState(ntfyService.getConnectionState())
        }, 1000)

        return () => {
            unsubscribe()
            clearInterval(interval)
            ntfyService.stop()
        }
    }, [user, handleNotification])

    const value: NtfyContextValue = {
        connectionState,
        lastNotification,
        isConnected: connectionState.status === 'connected'
    }

    return (
        <NtfyContext.Provider value={value}>
            {children}
        </NtfyContext.Provider>
    )
}

export default NtfyNotificationProvider
